import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getPushNotifications,
  sendPushNotification,
  sendBulkPushNotifications,
  sendTestPushNotification,
  getPushNotificationStats,
} from "../../../data";
import { AdminLayout } from "../shared/layout";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/components/ui/use-toast";
import {
  Bell,
  Plus,
  Users,
  Send,
  Smartphone,
  CheckCircle,
  XCircle,
  Activity,
} from "lucide-react";
import { format } from "date-fns";
import { tr } from "date-fns/locale";

export function PushNotificationsScreen() {
  const queryClient = useQueryClient();
  const [sendDialog, setSendDialog] = useState({ open: false, bulk: false });
  const [formData, setFormData] = useState({
    userId: "",
    userIds: "",
    title: "",
    body: "",
    platform: "all",
    data: "{}",
  });

  const { data, isLoading } = useQuery({
    queryKey: ["push-notifications"],
    queryFn: () => getPushNotifications(),
  });

  const { data: statsData } = useQuery({
    queryKey: ["push-notification-stats"],
    queryFn: getPushNotificationStats,
  });

  const sendMutation = useMutation({
    mutationFn: sendPushNotification,
    onSuccess: (response) => {
      queryClient.invalidateQueries(["push-notifications"]);
      setSendDialog({ open: false, bulk: false });
      resetForm();
      
      const result = response.data?.sendResult;
      toast({
        title: "Başarılı",
        description: `Push notification gönderildi. ${result?.devicesReached || 0} cihaza ulaştı.`,
        variant: "success",
      });
    },
    onError: (error) => {
      toast({
        title: "Hata",
        description: error.response?.data?.message || "Push notification gönderilemedi",
        variant: "destructive",
      });
    },
  });

  const bulkSendMutation = useMutation({
    mutationFn: sendBulkPushNotifications,
    onSuccess: (response) => {
      queryClient.invalidateQueries(["push-notifications"]);
      setSendDialog({ open: false, bulk: false });
      resetForm();
      
      const result = response.data;
      toast({
        title: "Başarılı",
        description: `${result.sent}/${result.total} kullanıcıya gönderildi`,
        variant: "success",
      });
    },
  });

  const testMutation = useMutation({
    mutationFn: sendTestPushNotification,
    onSuccess: () => {
      toast({
        title: "Başarılı",
        description: "Test notification gönderildi",
        variant: "success",
      });
    },
  });

  const resetForm = () => {
    setFormData({
      userId: "",
      userIds: "",
      title: "",
      body: "",
      platform: "all",
      data: "{}",
    });
  };

  const handleSubmit = () => {
    let customData = {};
    try {
      customData = JSON.parse(formData.data || "{}");
    } catch (error) {
      toast({
        title: "Hata",
        description: "Data JSON formatında olmalı",
        variant: "destructive",
      });
      return;
    }

    if (sendDialog.bulk) {
      const userIds = formData.userIds
        .split(",")
        .map((id) => parseInt(id.trim()))
        .filter((id) => !isNaN(id));

      bulkSendMutation.mutate({
        userIds,
        title: formData.title,
        body: formData.body,
        data: customData,
        platform: formData.platform,
      });
    } else {
      sendMutation.mutate({
        userId: parseInt(formData.userId),
        title: formData.title,
        body: formData.body,
        data: customData,
        platform: formData.platform,
      });
    }
  };

  const handleTestNotification = () => {
    testMutation.mutate({
      title: "Test Notification",
      body: "Bu bir test bildirimidir",
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return format(new Date(dateString), "dd MMM yyyy, HH:mm", { locale: tr });
    } catch (error) {
      return dateString;
    }
  };

  const notifications = data?.data?.data || [];
  const stats = statsData?.data?.data?.overview || {};

  const actions = [
    {
      name: "Test Gönder",
      icon: <Smartphone className="mr-2 h-4 w-4" />,
      onClick: handleTestNotification,
    },
    {
      name: "Tek Kullanıcıya",
      icon: <Plus className="mr-2 h-4 w-4" />,
      onClick: () => {
        setSendDialog({ open: true, bulk: false });
        resetForm();
      },
    },
    {
      name: "Toplu Gönder",
      icon: <Users className="mr-2 h-4 w-4" />,
      onClick: () => {
        setSendDialog({ open: true, bulk: true });
        resetForm();
      },
    },
  ];

  return (
    <AdminLayout
      title="Push Notifications"
      description="Kullanıcılara push notification gönderin (FCM)"
      loading={isLoading}
      actions={actions}
    >
      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-4 mb-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Toplam</CardTitle>
            <Bell className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.totalNotifications || 0}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Gönderildi</CardTitle>
            <CheckCircle className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {stats.sentNotifications || 0}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Başarısız</CardTitle>
            <XCircle className="h-4 w-4 text-red-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">
              {stats.failedNotifications || 0}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Başarı Oranı</CardTitle>
            <Activity className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.successRate || 0}%</div>
          </CardContent>
        </Card>
      </div>

      {/* Notifications Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Bell className="mr-2 h-5 w-5" />
            Push Notifications ({notifications.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {notifications.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Bell className="mx-auto h-12 w-12 mb-4 text-gray-300" />
              <p>Henüz push notification bulunmuyor.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Başlık</TableHead>
                  <TableHead>İçerik</TableHead>
                  <TableHead>Platform</TableHead>
                  <TableHead>Durum</TableHead>
                  <TableHead>Hedef</TableHead>
                  <TableHead>Tarih</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {notifications.map((notification) => (
                  <TableRow key={notification.id}>
                    <TableCell className="font-medium">{notification.title}</TableCell>
                    <TableCell>
                      <div className="max-w-xs truncate text-sm">{notification.body}</div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{notification.platform || "all"}</Badge>
                    </TableCell>
                    <TableCell>
                      {notification.status === "SENT" ? (
                        <Badge variant="success">
                          <CheckCircle className="h-3 w-3 mr-1" />
                          Gönderildi
                        </Badge>
                      ) : (
                        <Badge variant="destructive">
                          <XCircle className="h-3 w-3 mr-1" />
                          Başarısız
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-gray-500">
                      {notification.targetType === "user" ? "Kullanıcı" : "Grup"}
                    </TableCell>
                    <TableCell>{formatDate(notification.sentAt)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Send Dialog */}
      <Dialog
        open={sendDialog.open}
        onOpenChange={(open) => {
          setSendDialog({ ...sendDialog, open });
          if (!open) resetForm();
        }}
      >
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {sendDialog.bulk ? "Toplu Push Notification" : "Push Notification Gönder"}
            </DialogTitle>
            <DialogDescription>
              {sendDialog.bulk
                ? "Birden fazla kullanıcıya push notification gönderin"
                : "Tek bir kullanıcıya push notification gönderin"}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {sendDialog.bulk ? (
              <div className="space-y-2">
                <Label htmlFor="userIds">User IDs * (virgülle ayırın)</Label>
                <Input
                  id="userIds"
                  placeholder="1, 2, 3, 4"
                  value={formData.userIds}
                  onChange={(e) => setFormData({ ...formData, userIds: e.target.value })}
                />
              </div>
            ) : (
              <div className="space-y-2">
                <Label htmlFor="userId">User ID *</Label>
                <Input
                  id="userId"
                  type="number"
                  placeholder="1"
                  value={formData.userId}
                  onChange={(e) => setFormData({ ...formData, userId: e.target.value })}
                />
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="title">Başlık *</Label>
              <Input
                id="title"
                placeholder="Notification başlığı"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="body">Mesaj *</Label>
              <Textarea
                id="body"
                placeholder="Notification mesajı..."
                value={formData.body}
                onChange={(e) => setFormData({ ...formData, body: e.target.value })}
                rows={3}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="platform">Platform</Label>
              <Select
                value={formData.platform}
                onValueChange={(value) => setFormData({ ...formData, platform: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tümü</SelectItem>
                  <SelectItem value="ios">iOS</SelectItem>
                  <SelectItem value="android">Android</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="data">Custom Data (JSON)</Label>
              <Textarea
                id="data"
                placeholder='{"screen": "Home", "action": "open"}'
                value={formData.data}
                onChange={(e) => setFormData({ ...formData, data: e.target.value })}
                rows={3}
              />
              <p className="text-xs text-gray-500">
                Deep linking ve custom actions için JSON formatında veri
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setSendDialog({ open: false, bulk: false });
                resetForm();
              }}
            >
              İptal
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={
                !formData.title ||
                !formData.body ||
                (!sendDialog.bulk && !formData.userId) ||
                (sendDialog.bulk && !formData.userIds) ||
                sendMutation.isLoading ||
                bulkSendMutation.isLoading
              }
            >
              <Send className="mr-2 h-4 w-4" />
              {sendMutation.isLoading || bulkSendMutation.isLoading
                ? "Gönderiliyor..."
                : "Gönder"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
